import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, typeEventArgs } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CachedSession, KeycloakOfflineService } from './keycloak-offline.service';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  planCode: PlanCode;
  mandantId: string;
  roles: string[];
  trialStartDate: string | null;
}

export type PlanCode = 'starter' | 'pro' | 'premium';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  protected readonly http = inject(HttpClient);
  protected readonly keycloak = inject(Keycloak);
  protected readonly router = inject(Router);
  protected readonly kcEvent = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly offlineService = inject(KeycloakOfflineService);

  // ── Private Signals ──────────────────────────────────────
  protected readonly _user = signal<AuthUser | null>(null);
  protected readonly _loading = signal(true);
  protected readonly _provisioning = signal(false);
  private readonly TRIAL_DAYS = 7;

  readonly _isOffline = signal(false);

  // ── Public Signals ──────────────────────────────────────
  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly provisioning = this._provisioning.asReadonly();
  readonly isOffline = this._isOffline.asReadonly();

  readonly trialStartDate = computed(() => this._user()?.trialStartDate ?? null);
  readonly trialDaysLeft = computed(() => {
    const start = this.trialStartDate();
    if(!start) return 0;
    const diff = Date.now() - new Date(start).getTime();
    const elapsed = Math.floor(diff / (1000 * 60 * 60 * 24));
    return Math.max(0, this.TRIAL_DAYS - elapsed);
  })
  readonly isTrialActive = computed(() => this.trialDaysLeft() > 0);
  readonly trialExpired = computed(() => {
    const start = this.trialStartDate();
    if(!start) return false;
    return !this.isTrialActive();
  })

  readonly canAccess = computed(() => (requiredPlan: PlanCode): boolean => {
    if(this.isTrialActive()) return true;
    return this.hasRole(requiredPlan);
  });

  readonly loggedIn = computed(() => this.user() !== null);

  // --- Computed Plan-Signals -------------------------------
  readonly planCode = computed<PlanCode>(
    () => this._user()?.planCode ?? 'starter'
  )

  readonly mandantId = computed(() => this._user()?.mandantId ?? '');

  private readonly planLevel = computed(() =>
    ({starter: 0, pro: 1, premium: 2}[this.planCode()] ?? 0)
  )

  readonly isPro = computed(() => this.planLevel() >= 1);
  readonly isPremium = computed(() => this.planLevel() >= 2);

  private offlineRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly OFFLINE_RETRY_DELAY = 5000;
  private loginLoggedThisSession = false;

  constructor() {
   effect(() => {
     const event = this.kcEvent();

     switch (event.type) {
       case KeycloakEventType.Ready: {
         const isAuthenticated = typeEventArgs<boolean>(event.args);
         if (isAuthenticated) {
           this.cancelOfflineRetry();
           this.loadUser();
         } else {
           this.tryOfflineFallback();
           this._loading.set(false);
         }
         break;
       }

       case KeycloakEventType.AuthSuccess:
         this.cancelOfflineRetry();
         this._isOffline.set(false);
         this.loadUser();
         break;

       case KeycloakEventType.AuthLogout:
         // Beim Logout auch den Cache leeren
         this.cancelOfflineRetry();
         this.offlineService.remove();
         this._user.set(null);
         this._isOffline.set(false);
         this._loading.set(false);
         break;

       case KeycloakEventType.AuthError:
         this.scheduleOfflineFallback();
         break;

       case KeycloakEventType.TokenExpired:
         void this.tryTokenRefresh();
         break;
     }
   })
  }

 // Offline Fallback --------------------------
 private cancelOfflineRetry(): void {
    if (this.offlineRetryTimeout) {
      clearTimeout(this.offlineRetryTimeout);
      this.offlineRetryTimeout = null;
    }
 }

 private scheduleOfflineFallback(): void {
    if (this.offlineRetryTimeout) return;
    console.log('[SmartSheep] AuthError - versuche Token-Refresh in 5s...');

    this.offlineRetryTimeout = setTimeout(async () => {
      this.offlineRetryTimeout = null;
      try {
        const refreshed = await this.keycloak.updateToken(30);
        if(refreshed) {
          console.log('[SmartSheep] Token erfolgreich aktualisiert');
          this._isOffline.set(false);
          await this.loadUser();
          return;
        }
      } catch {
        console.warn('[SmartSheep] Token konnte nicht aktualisiert werden');
      }
      this.tryOfflineFallback();
      this._loading.set(false);
    }, this.OFFLINE_RETRY_DELAY);
 }

 private async tryTokenRefresh(): Promise<void> {
    try {
      const refreshed = await this.keycloak.updateToken(30);
      if(refreshed) {
        this._isOffline.set(false);
        await this.loadUser();
        console.log('[SmartSheep] Token erfolgreich aktualisiert');
      }
    } catch {
      console.warn('[SmartSheep] TokenExpired - Refresh fehlgeschlagen');
      this.scheduleOfflineFallback();
    }
 }

 private tryOfflineFallback(): void {
    const cached = this.offlineService.get();

    if(!cached) {
      this._loading.set(false);
      return;
    }

    //Session abgelaufen -> Cache löschen, kein Zugang
   if(!this.offlineService.isSessionStillValid()) {
     console.warn('[SmartSheep] Offline-Session abgelaufen - Login erforderlich');
     console.log('[SmartSheep] - Wieviel Tage noch offline', this.offlineService.getOfflineDays())
     this.offlineService.remove();
     this._loading.set(false);
     return;
   }

   this._user.set(this.mapCachedToAuthUser(cached));
   this._isOffline.set(true);
   this._loading.set(false);
 }

 private mapCachedToAuthUser(cached: CachedSession): AuthUser {
    return {
      id: cached.id,
      fullName: cached.fullName,
      firstName: cached.firstName,
      lastName: cached.lastName,
      email: cached.email,
      mandantId: cached.mandantId,
      roles: cached.roles,
      planCode: cached.planCode,
      trialStartDate: cached.trialStartDate,
    };
 }

  private async loadUser(): Promise<void> {
    const token = this.keycloak.tokenParsed as any;
    if(!token) { this._loading.set(false); return}

    // --- mandantId noch nicht gesetzt = neuer User
    if(!token.mandantId) {
      await this.provision(token);
      return;
    }

    const user: AuthUser = {
      id: token.sub,
      fullName: token.name,
      mandantId: token.mandantId,
      roles:token.realm_access?.roles ?? [],
      planCode: token.planCode,
      email: token.email,
      trialStartDate: token.trialStartDate ?? null,
    }

      this._user.set(user);
      this._loading.set(false);
      this.logLoginEvent();

      this.offlineService.save({
        id: user.id,
        fullName: user.fullName,
        firstName: token.given_name ?? '',
        lastName: token.family_name ?? '',
        email: user.email,
        mandantId: user.mandantId,
        roles: user.roles,
        planCode: user.planCode,
        trialStartDate: user.trialStartDate
      })
  }

  // Meldet einen Login ans Logbuch - nur einmal pro App-Sitzung, nicht bei jedem Token-Refresh.
  private logLoginEvent(): void {
    if (this.loginLoggedThisSession) return;
    this.loginLoggedThisSession = true;

    this.http.post(`${environment.apiUrlV1}/auth/login`, {}).subscribe({
      error: (err) => console.warn('[SmartSheep] Login konnte nicht protokolliert werden', err),
    });
  }

  private async provision(token: any): Promise<void> {
    this._provisioning.set(true);

    const minDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    try {
      await Promise.all([
        firstValueFrom(this.http.post(`${environment.apiUrlV1}/auth/provision`, {})),
        minDelay(25500), // 10+8+5+2s + 500ms Puffer
      ]);

      // --- Token aktualisieren damit mandantId drin ist -------------
      await this.keycloak.updateToken(-1);
      // --- Seite neu laden damit der neuen Token greift -----------

      await this.keycloak.updateToken(-1);
      await this.loadUser();
      window.location.reload();
      return;
    } catch (error) {
      console.error('Fehler beim Abrufen der Benutzerdaten', error);
      this._loading.set(false);
    } finally {
      this._provisioning.set(false);
    }
  }

  hasRole(required: PlanCode):boolean {
    const levels: Record<PlanCode, number> = {starter: 0, pro: 1, premium: 2};
    return (levels[this.planCode()] ?? 0) >= levels[required];
  }

  async login(): Promise<void> {
    await this.keycloak.login({ redirectUri: window.location.href });
  }

  async logout(): Promise<void> {
    this.cancelOfflineRetry();
    this.offlineService.remove();
    this._user.set(null);
    this._isOffline.set(false);
    this.loginLoggedThisSession = false;
    await this.keycloak.logout({ redirectUri: window.location.origin });
  }
}
